/* eslint-disable react/no-array-index-key */
import { Box, Typography } from '@mui/material';
import React from 'react';

import { ChromeTheme, DeviceType, SiteContextType } from '../../types/previewStudio';

interface SiteContextProps {
  type: SiteContextType;
  adSlot: React.ReactNode;
  deviceType: DeviceType;
  chromeTheme?: ChromeTheme;
}

function NewsSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  const divider = '#e2e2e2';

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      {/* Forbes nav bar */}
      <Box sx={{ bgcolor: '#000', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Hamburger */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: 18 }}>
          <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
        </Box>
        {/* Forbes logo */}
        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.1rem', fontFamily: "'Spectral', Georgia, serif", letterSpacing: 2 }}>
          Forbes
        </Typography>
        {/* Search */}
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.7)', position: 'relative' }}>
          <Box sx={{ width: 5, height: 1.5, bgcolor: 'rgba(255,255,255,0.7)', position: 'absolute', bottom: 1, right: 0, transform: 'rotate(45deg)', borderRadius: 1 }} />
        </Box>
      </Box>

      {/* Category nav */}
      <Box sx={{ display: 'flex', gap: 2, px: 1.5, py: 0.6, borderBottom: `1px solid ${divider}`, overflow: 'hidden', bgcolor: '#fff' }}>
        {['Featured', 'Leadership', 'Money', 'Business', 'Small Business', 'Lifestyle'].map((cat, i) => (
          <Typography key={cat} variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 500, color: i === 0 ? '#000' : '#666', whiteSpace: 'nowrap' }}>
            {cat}
          </Typography>
        ))}
      </Box>

      {/* Hero article */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ height: 190, position: 'relative', overflow: 'hidden' }}>
          <Box component="img" src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=220&fit=crop" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '70%', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5 }}>
            <Typography sx={{ color: '#f0c75e', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}>
              Featured
            </Typography>
            <Typography sx={{ color: 'white', fontFamily: "'Spectral', Georgia, serif", fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25, mb: 0.5 }}>
              Global Leaders Gather For Historic Climate Summit In Geneva
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.5rem' }}>
              Sarah Mitchell &middot; Forbes Staff
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Article list */}
      <Box sx={{ px: 1.5 }}>
        {/* Article 1 */}
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: `1px solid ${divider}` }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#888', fontSize: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
              Innovation
            </Typography>
            <Typography sx={{ color: '#222', fontFamily: "'Spectral', Georgia, serif", fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3, mb: 0.4 }}>
              Tech Giants Report Record Earnings Amid AI Investment Boom
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.48rem' }}>
              James Park &middot; 4 hours ago
            </Typography>
          </Box>
          <Box component="img" src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=160&h=120&fit=crop" alt="" sx={{ width: 85, height: 65, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
        </Box>

        {/* Article 2 */}
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: `1px solid ${divider}` }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#888', fontSize: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
              Money
            </Typography>
            <Typography sx={{ color: '#222', fontFamily: "'Spectral', Georgia, serif", fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3, mb: 0.4 }}>
              Federal Reserve Signals Extended Rate Pause Through 2026
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.48rem' }}>
              Dr. Emily Chen &middot; 5 hours ago
            </Typography>
          </Box>
          <Box component="img" src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=160&h=120&fit=crop" alt="" sx={{ width: 85, height: 65, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
        </Box>
      </Box>

      {/* Ad — Forbes style: grey bg, "Advertisement" label */}
      <Box sx={{ bgcolor: '#f7f7f7', borderTop: `1px solid ${divider}`, borderBottom: `1px solid ${divider}` }}>
        <Typography sx={{ color: '#bbb', fontSize: '0.42rem', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'center', pt: 0.5 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* More articles */}
      <Box sx={{ px: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: `1px solid ${divider}` }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#888', fontSize: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
              Lifestyle
            </Typography>
            <Typography sx={{ color: '#222', fontFamily: "'Spectral', Georgia, serif", fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3, mb: 0.4 }}>
              New Study Finds Link Between Sleep Patterns And Longevity
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.48rem' }}>
              Maria Rodriguez &middot; 6 hours ago
            </Typography>
          </Box>
          <Box component="img" src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=160&h=120&fit=crop" alt="" sx={{ width: 85, height: 65, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: `1px solid ${divider}` }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#888', fontSize: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
              Business
            </Typography>
            <Typography sx={{ color: '#222', fontFamily: "'Spectral', Georgia, serif", fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3, mb: 0.4 }}>
              Award Season Kicks Off With Surprising Nominations List
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.48rem' }}>
              David Kim &middot; 8 hours ago
            </Typography>
          </Box>
          <Box component="img" src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=160&h=120&fit=crop" alt="" sx={{ width: 85, height: 65, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
        </Box>

        {/* Daily Cover / Trending */}
        <Box sx={{ py: 1.5 }}>
          <Typography sx={{ color: '#222', fontWeight: 800, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.5, mb: 1 }}>
            Daily Cover
          </Typography>
          <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
            <Box component="img" src="https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=400&h=160&fit=crop" alt="" sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1, background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
              <Typography sx={{ color: 'white', fontFamily: "'Spectral', Georgia, serif", fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.3 }}>
                The Billionaires Betting Big On Space Tourism
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function EspnSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  const scores = [
    { away: 'LAL', home: 'BOS', awayScore: 108, homeScore: 112, status: 'Final' },
    { away: 'NYY', home: 'CHC', awayScore: 4, homeScore: 7, status: 'Final' },
    { away: 'KC', home: 'PHI', awayScore: 21, homeScore: 17, status: '4th 2:30' },
  ];

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      {/* ESPN header */}
      <Box sx={{ bgcolor: '#d00', px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Hamburger */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: 18 }}>
            <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
            <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
            <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
          </Box>
          {/* ESPN logo */}
          <Box component="img" src="/gallery-assets/ESPN-logo.png" alt="ESPN" sx={{ height: 18, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </Box>
        {/* Right side icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Search */}
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.8)', position: 'relative' }}>
            <Box sx={{ width: 4, height: 1.5, bgcolor: 'rgba(255,255,255,0.8)', position: 'absolute', bottom: 0, right: -1, transform: 'rotate(45deg)', borderRadius: 1 }} />
          </Box>
          {/* User icon */}
          <Box sx={{ width: 16, height: 16, position: 'relative' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.8)', mx: 'auto' }} />
            <Box sx={{ width: 14, height: 6, borderRadius: '50% 50% 0 0', border: '1.5px solid rgba(255,255,255,0.8)', borderBottom: 'none', mt: '1px', mx: 'auto' }} />
          </Box>
        </Box>
      </Box>

      {/* Score ticker */}
      <Box sx={{ bgcolor: '#1a1a1a', display: 'flex', overflow: 'hidden' }}>
        {scores.map((game, i) => (
          <Box key={i} sx={{ px: 1.5, py: 0.75, borderRight: '1px solid #333', minWidth: 100, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography sx={{ color: '#aaa', fontSize: '0.55rem', fontWeight: 600 }}>{game.away}</Typography>
              <Typography sx={{ color: 'white', fontSize: '0.55rem', fontWeight: 700 }}>{game.awayScore}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#aaa', fontSize: '0.55rem', fontWeight: 600 }}>{game.home}</Typography>
              <Typography sx={{ color: 'white', fontSize: '0.55rem', fontWeight: 700 }}>{game.homeScore}</Typography>
            </Box>
            <Typography sx={{ color: game.status === 'Final' ? '#888' : '#4caf50', fontSize: '0.45rem', mt: 0.25 }}>{game.status}</Typography>
          </Box>
        ))}
      </Box>

      {/* Nav tabs */}
      <Box sx={{ display: 'flex', gap: 2, px: 1.5, py: 0.6, borderBottom: '2px solid #e8e8e8', bgcolor: '#fff' }}>
        {['Top Headlines', 'NFL', 'NBA', 'MLB', 'Soccer'].map((tab, i) => (
          <Typography key={tab} sx={{ fontSize: '0.55rem', fontWeight: i === 0 ? 700 : 500, color: i === 0 ? '#d00' : '#666', whiteSpace: 'nowrap', borderBottom: i === 0 ? '2px solid #d00' : 'none', pb: 0.3, mb: '-2px' }}>
            {tab}
          </Typography>
        ))}
      </Box>

      {/* Top story */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ height: 170, overflow: 'hidden' }}>
          <Box component="img" src="https://images.unsplash.com/photo-1471295253337-3ceaaedca402?w=400&h=200&fit=crop" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5 }}>
            <Typography sx={{ color: '#d00', fontSize: '0.5rem', fontWeight: 800, textTransform: 'uppercase', mb: 0.3 }}>
              TOP HEADLINES
            </Typography>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.25 }}>
              Championship Series Set After Historic Playoff Weekend
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stories */}
      <Box sx={{ px: 1.5 }}>
        {[
          { headline: 'Trade Deadline: Five Blockbuster Deals That Could Reshape the League', img: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=160&h=120&fit=crop' },
          { headline: 'Rookie Sensation Breaks 40-Year-Old Record in Dominant Performance', img: 'https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?w=160&h=120&fit=crop' },
        ].map((story, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: '1px solid #e8e8e8' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.3 }}>
                {story.headline}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.48rem', mt: 0.3 }}>
                ESPN Staff &middot; {i + 2}h ago
              </Typography>
            </Box>
            <Box component="img" src={story.img} alt="" sx={{ width: 80, height: 60, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
          </Box>
        ))}
      </Box>

      {/* Ad */}
      <Box sx={{ bgcolor: '#f4f4f4', borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8' }}>
        <Typography sx={{ color: '#bbb', fontSize: '0.42rem', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'center', pt: 0.5 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* More stories */}
      <Box sx={{ px: 1.5 }}>
        {[
          { headline: 'Injury Report: Star Quarterback Listed as Questionable for Sunday', img: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=160&h=120&fit=crop' },
          { headline: 'Transfer Window: European Giants Circle American Midfielder', img: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=160&h=120&fit=crop' },
        ].map((story, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: '1px solid #e8e8e8' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.3 }}>
                {story.headline}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.48rem', mt: 0.3 }}>
                ESPN Staff &middot; {i + 5}h ago
              </Typography>
            </Box>
            <Box component="img" src={story.img} alt="" sx={{ width: 80, height: 60, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function AllRecipesSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  return (
    <Box sx={{ bgcolor: '#fff' }}>
      {/* AllRecipes header */}
      <Box sx={{ bgcolor: '#fff', px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: 18 }}>
          <Box sx={{ bgcolor: '#333', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: '#333', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: '#333', height: 2, borderRadius: 1 }} />
        </Box>
        <Box component="img" src="https://www.google.com/s2/favicons?domain=allrecipes.com&sz=128" alt="Allrecipes" sx={{ height: 22, width: 22, objectFit: 'contain' }} />
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid #666', position: 'relative' }}>
          <Box sx={{ width: 5, height: 1.5, bgcolor: '#666', position: 'absolute', bottom: 1, right: 0, transform: 'rotate(45deg)', borderRadius: 1 }} />
        </Box>
      </Box>

      {/* Category pills */}
      <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, overflow: 'hidden' }}>
        {['Dinner', 'Quick & Easy', 'Healthy', 'Desserts', 'Soups'].map((cat, i) => (
          <Box key={cat} sx={{ bgcolor: i === 0 ? '#d62828' : '#f0f0f0', borderRadius: 999, px: 1.25, py: 0.3, flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: i === 0 ? 'white' : '#444' }}>{cat}</Typography>
          </Box>
        ))}
      </Box>

      {/* Featured recipe */}
      <Box sx={{ position: 'relative' }}>
        <Box component="img" src="https://images.unsplash.com/photo-1532550907401-a500c9a57435?w=400&h=220&fit=crop" alt="" sx={{ width: '100%', height: 170, objectFit: 'cover', display: 'block' }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5, background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.25, mb: 0.3 }}>
            One-Pan Garlic Butter Chicken With Roasted Vegetables
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: '#ffc107', fontSize: '0.55rem' }}>★★★★★</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.45rem' }}>1,247 ratings</Typography>
          </Box>
        </Box>
      </Box>

      {/* Recipe cards */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#222', mb: 1 }}>
          Trending Now
        </Typography>
        {[
          { title: 'Slow Cooker Beef Stew', time: '4 hrs 20 min', rating: '4.8', img: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=160&h=120&fit=crop' },
          { title: 'Classic Banana Bread', time: '1 hr 15 min', rating: '4.9', img: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=160&h=120&fit=crop' },
        ].map((recipe, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: '1px solid #e8e8e8' }}>
            <Box component="img" src={recipe.img} alt="" sx={{ width: 85, height: 65, borderRadius: 1, flexShrink: 0, objectFit: 'cover' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.3, mb: 0.3 }}>
                {recipe.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: '#ffc107', fontSize: '0.5rem' }}>★ {recipe.rating}</Typography>
                <Typography sx={{ color: '#999', fontSize: '0.45rem' }}>&middot; {recipe.time}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Ad */}
      <Box sx={{ bgcolor: '#f8f8f8', borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8' }}>
        <Typography sx={{ color: '#bbb', fontSize: '0.42rem', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'center', pt: 0.5 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* More recipes */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#222', mb: 1 }}>
          Popular This Week
        </Typography>
        {[
          { title: 'Sheet Pan Lemon Herb Salmon', time: '30 min', rating: '4.7', img: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=160&h=120&fit=crop' },
          { title: 'Homemade Pizza Dough', time: '2 hrs', rating: '4.9', img: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=160&h=120&fit=crop' },
        ].map((recipe, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: '1px solid #e8e8e8' }}>
            <Box component="img" src={recipe.img} alt="" sx={{ width: 85, height: 65, borderRadius: 1, flexShrink: 0, objectFit: 'cover' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.3, mb: 0.3 }}>
                {recipe.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: '#ffc107', fontSize: '0.5rem' }}>★ {recipe.rating}</Typography>
                <Typography sx={{ color: '#999', fontSize: '0.45rem' }}>&middot; {recipe.time}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function InstagramSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  return (
    <Box sx={{ bgcolor: '#fafafa' }}>
      {/* Instagram header */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dbdbdb', px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="img" src="https://www.google.com/s2/favicons?domain=instagram.com&sz=128" alt="Instagram" sx={{ height: 20, width: 20, objectFit: 'contain' }} />
          <Typography sx={{ fontFamily: "'Segoe UI', Arial, sans-serif", fontWeight: 700, fontSize: '0.9rem', color: '#262626' }}>
            Instagram
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Add post (+) */}
          <Box sx={{ width: 18, height: 18, border: '1.5px solid #262626', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1, color: '#262626', fontWeight: 300 }}>+</Typography>
          </Box>
          {/* Heart icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
          {/* DM / Paper airplane */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
        </Box>
      </Box>

      {/* Stories */}
      <Box sx={{ display: 'flex', gap: 1.25, px: 1.5, py: 1, bgcolor: '#fff', borderBottom: '1px solid #dbdbdb', overflow: 'hidden' }}>
        {[1074, 1027, 64, 1012, 1025].map((id, i) => (
          <Box key={i} sx={{ textAlign: 'center', flexShrink: 0 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box component="img" src={`https://picsum.photos/id/${id}/76/76`} alt="" sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid white' }} />
            </Box>
            <Typography sx={{ fontSize: '0.4rem', color: '#262626', mt: 0.25 }}>
              {['Your Story', 'travel_co', 'foodie', 'nature', 'design'][i]}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Post 1 */}
      <Box sx={{ bgcolor: '#fff', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Box component="img" src="https://picsum.photos/id/1005/56/56" alt="" sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
          <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: '#262626' }}>wanderlust_photo</Typography>
        </Box>
        <Box sx={{ px: 0.5 }}>
          <Box component="img" src="https://picsum.photos/id/1018/375/375" alt="" sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', borderRadius: 0.5 }} />
        </Box>
        <Box sx={{ px: 1.5, py: 0.75 }}>
          {/* Action icons row */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5, alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.55rem', color: '#262626', mb: 0.25 }}>2,847 likes</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: '#262626' }}>
            <strong>wanderlust_photo</strong> Exploring hidden gems in the mountains ⛰️
          </Typography>
          <Typography sx={{ fontSize: '0.48rem', color: '#8e8e8e', mt: 0.25 }}>View all 43 comments</Typography>
          <Typography sx={{ fontSize: '0.42rem', color: '#8e8e8e', mt: 0.25 }}>2 HOURS AGO</Typography>
        </Box>
      </Box>

      {/* Sponsored post with ad */}
      <Box sx={{ bgcolor: '#fff', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Box component="img" src="https://picsum.photos/id/1062/56/56" alt="" sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: '#262626' }}>brand_official</Typography>
            <Typography sx={{ fontSize: '0.45rem', color: '#8e8e8e' }}>Sponsored</Typography>
          </Box>
          <Typography sx={{ color: '#8e8e8e', fontSize: '0.8rem' }}>•••</Typography>
        </Box>
        {adSlot}
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5, alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
          </Box>
        </Box>
      </Box>

      {/* Post 2 */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Box component="img" src="https://picsum.photos/id/1027/56/56" alt="" sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
          <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: '#262626' }}>city_vibes</Typography>
        </Box>
        <Box sx={{ px: 0.5 }}>
          <Box component="img" src="https://picsum.photos/id/1029/375/375" alt="" sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', borderRadius: 0.5 }} />
        </Box>
      </Box>
    </Box>
  );
}

// ---- People (Entertainment News) ----

function PeopleSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  const divider = '#e5e5e5';
  const peopleBlu = '#6ec6e6';
  const peopleYellow = '#f5c518';

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      {/* People header */}
      <Box sx={{ bgcolor: '#fff', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `3px solid ${peopleBlu}` }}>
        <Box component="img" src="/gallery-assets/People-Magazine-Emblem.png" alt="People" sx={{ height: 28, objectFit: 'contain' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: `1.5px solid ${peopleBlu}`, position: 'relative' }}>
            <Box sx={{ width: 5, height: 1.5, bgcolor: peopleBlu, position: 'absolute', bottom: 1, right: 0, transform: 'rotate(45deg)', borderRadius: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: 18 }}>
            <Box sx={{ bgcolor: peopleBlu, height: 2, borderRadius: 1 }} />
            <Box sx={{ bgcolor: peopleBlu, height: 2, borderRadius: 1 }} />
            <Box sx={{ bgcolor: peopleBlu, height: 2, borderRadius: 1 }} />
          </Box>
        </Box>
      </Box>

      {/* Category nav */}
      <Box sx={{ display: 'flex', gap: 2, px: 1.5, py: 0.6, borderBottom: `1px solid ${divider}`, overflow: 'hidden' }}>
        {['Celebrity', 'Royals', 'Style', 'Beauty', 'Movies', 'TV'].map((cat, i) => (
          <Typography key={cat} sx={{ fontSize: '0.55rem', fontWeight: i === 0 ? 700 : 400, color: i === 0 ? peopleBlu : '#666', whiteSpace: 'nowrap' }}>
            {cat}
          </Typography>
        ))}
      </Box>

      {/* Hero */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ height: 200, overflow: 'hidden' }}>
          <Box component="img" src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=220&fit=crop" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}>
            <Typography sx={{ color: peopleYellow, fontSize: '0.5rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, mb: 0.3 }}>
              Exclusive
            </Typography>
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2, fontFamily: 'Arial, sans-serif' }}>
              Inside the Star-Studded Gala That Had Everyone Talking
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stories */}
      <Box sx={{ px: 1.5 }}>
        {[
          { cat: 'CELEBRITY', headline: 'Fan-Favorite Couple Spotted Together at Surprise Beach Getaway', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=120&fit=crop' },
          { cat: 'MOVIES', headline: 'First Look: Blockbuster Sequel Drops Stunning New Trailer', img: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=160&h=120&fit=crop' },
        ].map((story, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: `1px solid ${divider}` }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: peopleBlu, fontSize: '0.45rem', fontWeight: 800, letterSpacing: 0.5, mb: 0.3 }}>
                {story.cat}
              </Typography>
              <Typography sx={{ color: '#222', fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.3 }}>
                {story.headline}
              </Typography>
            </Box>
            <Box component="img" src={story.img} alt="" sx={{ width: 85, height: 65, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
          </Box>
        ))}
      </Box>

      {/* Ad */}
      <Box sx={{ bgcolor: '#f8f8f8', borderTop: `1px solid ${divider}`, borderBottom: `1px solid ${divider}` }}>
        <Typography sx={{ color: '#bbb', fontSize: '0.42rem', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'center', pt: 0.5 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* More stories */}
      <Box sx={{ px: 1.5 }}>
        {[
          { cat: 'STYLE', headline: 'The Red Carpet Looks That Defined Award Season This Year', img: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=160&h=120&fit=crop' },
          { cat: 'TV', headline: 'Beloved Series Returns With a Shocking Season Premiere Twist', img: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=160&h=120&fit=crop' },
        ].map((story, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: `1px solid ${divider}` }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: peopleBlu, fontSize: '0.45rem', fontWeight: 800, letterSpacing: 0.5, mb: 0.3 }}>
                {story.cat}
              </Typography>
              <Typography sx={{ color: '#222', fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.3 }}>
                {story.headline}
              </Typography>
            </Box>
            <Box component="img" src={story.img} alt="" sx={{ width: 85, height: 65, borderRadius: 0.5, flexShrink: 0, objectFit: 'cover' }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---- Bleacher Report ----

function BleacherReportSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  return (
    <Box sx={{ bgcolor: '#000' }}>
      {/* BR header */}
      <Box sx={{ bgcolor: '#000', px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #222' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: 18 }}>
          <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: 'white', height: 2, borderRadius: 1 }} />
        </Box>
        <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '0.7rem', fontFamily: 'Arial, sans-serif', letterSpacing: 1 }}>
          BLEACHER REPORT
        </Typography>
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.5)' }} />
      </Box>

      {/* Sport tabs */}
      <Box sx={{ display: 'flex', gap: 0, overflow: 'hidden', borderBottom: '1px solid #222' }}>
        {['Top Stories', 'NFL', 'NBA', 'World Football', 'MLB'].map((tab, i) => (
          <Box key={tab} sx={{ px: 1.5, py: 0.6, bgcolor: i === 0 ? '#1a1a1a' : 'transparent', borderBottom: i === 0 ? '2px solid #4caf50' : 'none' }}>
            <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: i === 0 ? '#4caf50' : '#888', whiteSpace: 'nowrap' }}>
              {tab}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Hero card */}
      <Box sx={{ m: 1.5, borderRadius: 1.5, overflow: 'hidden', position: 'relative' }}>
        <Box component="img" src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=250&fit=crop" alt="" sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5, background: 'linear-gradient(transparent, rgba(0,0,0,0.9))' }}>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.85rem', lineHeight: 1.2 }}>
            Power Rankings: Biggest Risers and Fallers After Week 12
          </Typography>
          <Typography sx={{ color: '#888', fontSize: '0.45rem', mt: 0.5 }}>
            Tyler Conway &middot; 45 min ago
          </Typography>
        </Box>
      </Box>

      {/* Story cards */}
      {[
        { headline: 'Breaking: All-Star Guard Agrees to Max Extension With Contender', img: 'https://images.unsplash.com/photo-1515523110800-9415d13b84a8?w=160&h=120&fit=crop', time: '2h ago' },
        { headline: 'Draft Prospect Big Board: Top 25 Players With Stock Rising', img: 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=160&h=120&fit=crop', time: '3h ago' },
      ].map((story, i) => (
        <Box key={i} sx={{ mx: 1.5, mb: 1, bgcolor: '#111', borderRadius: 1.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 1.5, p: 1.25 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.68rem', lineHeight: 1.3 }}>
                {story.headline}
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.45rem', mt: 0.5 }}>
                B/R Staff &middot; {story.time}
              </Typography>
            </Box>
            <Box component="img" src={story.img} alt="" sx={{ width: 80, height: 60, borderRadius: 1, flexShrink: 0, objectFit: 'cover' }} />
          </Box>
        </Box>
      ))}

      {/* Ad */}
      <Box sx={{ mx: 1.5, mb: 1, bgcolor: '#111', borderRadius: 1.5, overflow: 'hidden' }}>
        <Typography sx={{ color: '#555', fontSize: '0.4rem', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'center', pt: 0.75 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* More stories */}
      {[
        { headline: 'Trade Grades: Evaluating Every Deal Before the Deadline', img: 'https://images.unsplash.com/photo-1519861531473-9200262188bf?w=160&h=120&fit=crop', time: '5h ago' },
        { headline: 'Rookie Watch: First-Year Players Making an Immediate Impact', img: 'https://images.unsplash.com/photo-1495563923587-bdc4282494d0?w=160&h=120&fit=crop', time: '6h ago' },
      ].map((story, i) => (
        <Box key={i} sx={{ mx: 1.5, mb: 1, bgcolor: '#111', borderRadius: 1.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 1.5, p: 1.25 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.68rem', lineHeight: 1.3 }}>
                {story.headline}
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.45rem', mt: 0.5 }}>
                B/R Staff &middot; {story.time}
              </Typography>
            </Box>
            <Box component="img" src={story.img} alt="" sx={{ width: 80, height: 60, borderRadius: 1, flexShrink: 0, objectFit: 'cover' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ---- MyRecipes ----

function MyRecipesSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  const divider = '#e8e8e8';

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      {/* MyRecipes header */}
      <Box sx={{ bgcolor: '#fff', px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${divider}` }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: 18 }}>
          <Box sx={{ bgcolor: '#333', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: '#333', height: 2, borderRadius: 1 }} />
          <Box sx={{ bgcolor: '#333', height: 2, borderRadius: 1 }} />
        </Box>
        <Typography sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'Georgia, serif' }}>
          MyRecipes
        </Typography>
        <Box sx={{ width: 18 }} />
      </Box>

      {/* Category nav */}
      <Box sx={{ display: 'flex', gap: 2, px: 1.5, py: 0.6, borderBottom: `1px solid ${divider}`, overflow: 'hidden' }}>
        {['Healthy', 'Quick & Easy', 'Comfort Food', 'Seasonal', 'Meal Prep'].map((cat, i) => (
          <Typography key={cat} sx={{ fontSize: '0.55rem', fontWeight: i === 0 ? 600 : 400, color: i === 0 ? '#2e7d32' : '#888', whiteSpace: 'nowrap' }}>
            {cat}
          </Typography>
        ))}
      </Box>

      {/* Featured recipe — editorial style */}
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
          <Box component="img" src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=250&fit=crop" alt="" sx={{ width: '100%', height: 170, objectFit: 'cover', display: 'block' }} />
        </Box>
        <Typography sx={{ color: '#2e7d32', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mt: 1.25, mb: 0.3 }}>
          Editor&apos;s Pick
        </Typography>
        <Typography sx={{ color: '#222', fontFamily: 'Georgia, serif', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3, mb: 0.3 }}>
          Mediterranean Grilled Chicken With Lemon Herb Quinoa
        </Typography>
        <Typography sx={{ color: '#999', fontSize: '0.48rem' }}>
          By Sarah Anderson &middot; 35 min &middot; ★ 4.8
        </Typography>
      </Box>

      {/* Recipe list */}
      <Box sx={{ px: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#222', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          What to Cook This Week
        </Typography>
        {[
          { title: 'Crispy Parmesan Crusted Salmon', time: '25 min', rating: '4.9', img: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=160&h=120&fit=crop' },
          { title: 'Roasted Cauliflower Tacos With Avocado Crema', time: '40 min', rating: '4.7', img: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=160&h=120&fit=crop' },
        ].map((recipe, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: `1px solid ${divider}` }}>
            <Box component="img" src={recipe.img} alt="" sx={{ width: 85, height: 65, borderRadius: 1, flexShrink: 0, objectFit: 'cover' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontFamily: 'Georgia, serif', fontWeight: 600, fontSize: '0.68rem', lineHeight: 1.3, mb: 0.3 }}>
                {recipe.title}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.45rem' }}>
                {recipe.time} &middot; ★ {recipe.rating}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Ad */}
      <Box sx={{ bgcolor: '#fafafa', borderTop: `1px solid ${divider}`, borderBottom: `1px solid ${divider}` }}>
        <Typography sx={{ color: '#ccc', fontSize: '0.42rem', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'center', pt: 0.5 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* More recipes */}
      <Box sx={{ px: 1.5, py: 1 }}>
        {[
          { title: 'Weeknight One-Pot Chicken and Rice', time: '45 min', rating: '4.8', img: 'https://images.unsplash.com/photo-1512058564366-18510be2db19?w=160&h=120&fit=crop' },
          { title: 'No-Bake Chocolate Peanut Butter Bars', time: '15 min', rating: '4.9', img: 'https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=160&h=120&fit=crop' },
        ].map((recipe, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: i === 0 ? `1px solid ${divider}` : 'none' }}>
            <Box component="img" src={recipe.img} alt="" sx={{ width: 85, height: 65, borderRadius: 1, flexShrink: 0, objectFit: 'cover' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontFamily: 'Georgia, serif', fontWeight: 600, fontSize: '0.68rem', lineHeight: 1.3, mb: 0.3 }}>
                {recipe.title}
              </Typography>
              <Typography sx={{ color: '#999', fontSize: '0.45rem' }}>
                {recipe.time} &middot; ★ {recipe.rating}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---- Facebook ----

function FacebookSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  return (
    <Box sx={{ bgcolor: '#f0f2f5' }}>
      {/* Facebook header */}
      <Box sx={{ bgcolor: '#fff', px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
        <Typography sx={{ color: '#1877f2', fontWeight: 900, fontSize: '1.2rem', fontFamily: 'Arial, sans-serif' }}>
          facebook
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Search */}
          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '1.5px solid #666', position: 'relative' }}>
              <Box sx={{ width: 4, height: 1.5, bgcolor: '#666', position: 'absolute', bottom: -2, right: -2, transform: 'rotate(45deg)' }} />
            </Box>
          </Box>
          {/* Messenger */}
          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#e4e6eb' }} />
        </Box>
      </Box>

      {/* Stories row */}
      <Box sx={{ display: 'flex', gap: 0.75, px: 1.5, py: 1, bgcolor: '#fff', mb: 1, overflow: 'hidden' }}>
        {[1074, 1027, 64, 1012].map((id, i) => (
          <Box key={i} sx={{ width: 70, height: 100, borderRadius: 1.5, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
            <Box component="img" src={`https://picsum.photos/id/${id}/140/200`} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 0.5, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))' }}>
              <Typography sx={{ color: 'white', fontSize: '0.4rem', fontWeight: 600 }}>
                {['Create Story', 'Alex K.', 'Travel Hub', 'News Daily'][i]}
              </Typography>
            </Box>
            {i === 0 && (
              <Box sx={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', width: 20, height: 20, borderRadius: '50%', bgcolor: '#1877f2', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: 'white', fontSize: '0.8rem', lineHeight: 1, fontWeight: 300 }}>+</Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Post 1 */}
      <Box sx={{ bgcolor: '#fff', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Box component="img" src="https://picsum.photos/id/1005/56/56" alt="" sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: '#050505' }}>Sarah Johnson</Typography>
            <Typography sx={{ fontSize: '0.45rem', color: '#65676b' }}>2h &middot; 🌐</Typography>
          </Box>
        </Box>
        <Typography sx={{ px: 1.5, pb: 0.75, fontSize: '0.6rem', color: '#050505' }}>
          Beautiful sunset from the hike today! Nature never disappoints 🌄
        </Typography>
        <Box component="img" src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop" alt="" sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
        <Box sx={{ px: 1.5, py: 0.5, display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e4e6eb' }}>
          <Typography sx={{ fontSize: '0.48rem', color: '#65676b' }}>👍❤️ 247</Typography>
          <Typography sx={{ fontSize: '0.48rem', color: '#65676b' }}>18 comments &middot; 5 shares</Typography>
        </Box>
        <Box sx={{ display: 'flex', px: 1.5, py: 0.5 }}>
          {['👍 Like', '💬 Comment', '↗ Share'].map((action) => (
            <Box key={action} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.55rem', color: '#65676b', fontWeight: 600 }}>{action}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Sponsored post with ad */}
      <Box sx={{ bgcolor: '#fff', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Box component="img" src="https://picsum.photos/id/1062/56/56" alt="" sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: '#050505' }}>Brand Partner</Typography>
            <Typography sx={{ fontSize: '0.45rem', color: '#65676b' }}>Sponsored &middot; 🌐</Typography>
          </Box>
          <Typography sx={{ color: '#65676b', fontSize: '0.8rem' }}>•••</Typography>
        </Box>
        {adSlot}
        <Box sx={{ display: 'flex', px: 1.5, py: 0.5, borderTop: '1px solid #e4e6eb' }}>
          {['👍 Like', '💬 Comment', '↗ Share'].map((action) => (
            <Box key={action} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.55rem', color: '#65676b', fontWeight: 600 }}>{action}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Post 2 */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Box component="img" src="https://picsum.photos/id/1027/56/56" alt="" sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: '#050505' }}>Mike Chen</Typography>
            <Typography sx={{ fontSize: '0.45rem', color: '#65676b' }}>5h &middot; 🌐</Typography>
          </Box>
        </Box>
        <Typography sx={{ px: 1.5, pb: 0.75, fontSize: '0.6rem', color: '#050505' }}>
          Just finished this amazing book! Highly recommend 📚
        </Typography>
        <Box component="img" src="https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=250&fit=crop" alt="" sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
      </Box>
    </Box>
  );
}

// ---- Words With Friends (In-App Interstitial) ----

function WordsWithFriendsSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  return (
    <Box sx={{ bgcolor: '#1a0a2e', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Game board background (blurred/dimmed) */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {/* Top HUD */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.75, bgcolor: 'rgba(0,0,0,0.3)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#e8a53a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'white', fontSize: '0.55rem', fontWeight: 800 }}>P1</Typography>
            </Box>
            <Box>
              <Typography sx={{ color: 'white', fontSize: '0.5rem', fontWeight: 600 }}>You</Typography>
              <Typography sx={{ color: '#e8a53a', fontSize: '0.7rem', fontWeight: 800 }}>142</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: '#888', fontSize: '0.45rem' }}>YOUR TURN</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box>
              <Typography sx={{ color: 'white', fontSize: '0.5rem', fontWeight: 600, textAlign: 'right' }}>Opponent</Typography>
              <Typography sx={{ color: '#5bbfea', fontSize: '0.7rem', fontWeight: 800, textAlign: 'right' }}>128</Typography>
            </Box>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#5bbfea', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'white', fontSize: '0.55rem', fontWeight: 800 }}>P2</Typography>
            </Box>
          </Box>
        </Box>

        {/* Dimmed game board grid */}
        <Box sx={{ flex: 1, opacity: 0.15, p: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(11, 1fr)', gap: '1px', height: '100%' }}>
            {Array.from({ length: 77 }).map((_, i) => {
              const hasLetter = [23, 24, 25, 34, 38, 44, 45, 46, 47, 55, 56].includes(i);
              const isSpecial = [0, 10, 33, 43, 66, 76, 5, 16, 27, 49, 60, 71].includes(i);
              return (
                <Box
                  key={i}
                  sx={{
                    bgcolor: (() => {
                      if (hasLetter) return '#e8a53a';
                      return isSpecial ? '#c0392b' : '#2d1b4e';
                    })(),
                    borderRadius: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 0,
                  }}
                >
                  {hasLetter && (
                    <Typography sx={{ color: 'white', fontSize: '0.4rem', fontWeight: 800 }}>
                      {['W', 'O', 'R', 'D', 'S', 'G', 'A', 'M', 'E', 'P', 'L'][i % 11]}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Interstitial ad overlay */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          width: '90%',
        }}>
          {/* Close button */}
          <Box sx={{ alignSelf: 'flex-end', mb: 0.25 }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'white', fontSize: '0.6rem', lineHeight: 1 }}>&times;</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.4rem', textTransform: 'uppercase', letterSpacing: 1.5 }}>
            Advertisement
          </Typography>
          {adSlot}
        </Box>

        {/* Bottom tile rack (dimmed) */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '2px', px: 2, py: 1, bgcolor: 'rgba(0,0,0,0.3)', opacity: 0.2 }}>
          {['S', 'T', 'A', 'R', 'E', 'N', 'I'].map((letter, i) => (
            <Box key={i} sx={{ width: 28, height: 28, bgcolor: '#e8a53a', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.65rem' }}>{letter}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ---- Subway Surfers (In-App Interstitial) ----

function SubwaySurfersSite({ adSlot }: Omit<SiteContextProps, 'type'>) {
  return (
    <Box sx={{ bgcolor: '#1a237e', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Sky gradient background */}
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #4fc3f7 0%, #0288d1 30%, #1a237e 70%, #0d1442 100%)' }} />

      {/* Game HUD (dimmed) */}
      <Box sx={{ position: 'relative', zIndex: 1, opacity: 0.2 }}>
        {/* Top bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: '#ff6f00' }} />
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.7rem', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>2,847</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#ffd600' }} />
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.65rem', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>156</Typography>
          </Box>
        </Box>

        {/* Score */}
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.5)', fontFamily: 'Arial, sans-serif' }}>
            28,450
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 2 }}>
            Score
          </Typography>
        </Box>
      </Box>

      {/* City buildings silhouette (dimmed) */}
      <Box sx={{ position: 'relative', zIndex: 1, flex: 1, opacity: 0.15 }}>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px', px: 1 }}>
          {[80, 120, 60, 140, 90, 110, 70, 130, 85, 100, 65].map((h, i) => (
            <Box key={i} sx={{ flex: 1, height: h, bgcolor: '#1a1a3e', borderRadius: '2px 2px 0 0', position: 'relative' }}>
              {/* Windows */}
              {Array.from({ length: Math.floor(h / 15) }).map((_, wi) => (
                <Box key={wi} sx={{ position: 'absolute', left: '25%', right: '25%', top: 4 + wi * 15, height: 6, bgcolor: wi % 3 === 0 ? 'rgba(255,235,59,0.3)' : 'rgba(255,255,255,0.05)', borderRadius: 0.25 }} />
              ))}
            </Box>
          ))}
        </Box>

        {/* Train tracks (dimmed) */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 30, bgcolor: '#333' }}>
          <Box sx={{ display: 'flex', height: '100%' }}>
            {[0, 1, 2].map((lane) => (
              <Box key={lane} sx={{ flex: 1, borderRight: lane < 2 ? '1px solid #555' : 'none', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: '40%', left: '10%', right: '10%', height: 2, bgcolor: '#666' }} />
                <Box sx={{ position: 'absolute', top: '60%', left: '10%', right: '10%', height: 2, bgcolor: '#666' }} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Interstitial ad overlay */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        width: '90%',
      }}>
        {/* Close button */}
        <Box sx={{ alignSelf: 'flex-end', mb: 0.25 }}>
          <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.6)', border: '1.5px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: 'white', fontSize: '0.7rem', lineHeight: 1, fontWeight: 300 }}>&times;</Typography>
          </Box>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.4rem', textTransform: 'uppercase', letterSpacing: 1.5 }}>
          Advertisement
        </Typography>
        {adSlot}
      </Box>

      {/* Pause overlay text (dimmed) */}
      <Box sx={{ position: 'absolute', bottom: 40, left: 0, right: 0, textAlign: 'center', zIndex: 1, opacity: 0.15 }}>
        <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: 3, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          Paused
        </Typography>
      </Box>
    </Box>
  );
}

export default function SiteContext({ type, adSlot, deviceType, chromeTheme = 'dark' }: SiteContextProps) {
  switch (type) {
    case 'allrecipes':
      return <AllRecipesSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'myrecipes':
      return <MyRecipesSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'forbes':
      return <NewsSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'people':
      return <PeopleSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'espn':
      return <EspnSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'bleacherreport':
      return <BleacherReportSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'instagram':
      return <InstagramSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'facebook':
      return <FacebookSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'wordswithfriends':
      return <WordsWithFriendsSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'subwaysurfers':
      return <SubwaySurfersSite adSlot={adSlot} deviceType={deviceType} chromeTheme={chromeTheme} />;
    case 'blank':
    default:
      return adSlot as JSX.Element;
  }
}
